/*
 * @fileOverView Dropdown component definition
 */

import Vue from 'vue';
import {TweenLite} from 'gsap';
import {KeyCodes, Events} from 'utils/Constants';

/**
 * Classes added and removed during dropdown manipulation.
 */
const CLASSES = {
  'IGNORE_VIEWPORT': 'js-dropdown_ignoreViewport',
  'OPEN_ABOVE': 'js-dropdown-list_openAbove'
}

/**
 * [Drodown Vue]
 * @type {class}
 */
Vue.component('dropdown-vue', {
  props: [
    'optionCount',
    'parentIndex',
    'parentSelected'
  ],

  data: function() {
    return {
      isExpanded: false,
      activeIndex: 0,
      selected: '',
      selectedIndex: '',
      maxIndex: this.optionCount - 1
    }
  },

  watch: {
    activeIndex: function (val) {
      if (this.activeIndex > this.maxIndex) {
        this.activeIndex = 0;
      }
      else if(this.activeIndex < 0) {
        this.activeIndex = this.maxIndex;
      }
      this.focusItem(this.activeIndex);
    },

    /**
     * React to an index change by a parent element, such as tabs.
     * @param {Integer} val
     */
    parentIndex: function (val) {
      if (this.$refs[val]) {
        this.selectItem(val);
      }
    },

  /**
   * React to an change by a parent element that is unaware of its index, such as a slider range.
   * @param {String} value
   */
    parentSelected: function (value) {
      this.set(value);
    },

    selected: function() {
      const $input = $(this.$refs.input);
      const $form = $input.closest('form');

      if ($form && $form.validate()) {
        this.$nextTick(() => {
          $input.valid();
        })
      }

      // Emit an event for parents that need to react, such as tabs.
      this.$emit('dropdown-changed', this.selectedIndex, this.selected, this.$el);
    }
  },

  mounted() {
    // Fixes setting default values.
    if (this.parentSelected) {
      this.set(this.parentSelected);
    }
  },

  methods: {
    /**
     * Handles toggling the dropdown visibility as triggers are used.
     *
     * @method toggleDropdown
     * @param {Object} event - Javascript event object
     * @public
     */
    toggleDropdown(e) {
      if (!this.isExpanded) {
        this.openDropdown();
      }
      else {
        this.closeDropdown();
      }
    },

    /**
     * Opens the dropdown by making the list visible.
     *
     * @method openDropdown
     * @public
     */
    openDropdown() {
      this.isExpanded = true;

      TweenLite.set(this.$refs.list, {height: 'auto'});

      if (this.willExtendBeyondViewport()) {
        this.$refs.list.classList.add(CLASSES.OPEN_ABOVE);
      }
      else {
        this.$refs.list.classList.remove(CLASSES.OPEN_ABOVE);
        TweenLite.from(this.$refs.list, 0.2, {
          height: 0,
          onComplete: () => {
            this.focusItem(this.selectedIndex);
          }
        });
      }

      document.body.addEventListener(Events.CLICK, e => this.handleOffClick(e));
      document.body.addEventListener(Events.KEYDOWN, e => this.handleBodyKeydown(e));
    },

    /**
     * Closes the dropdown by removing list visibility.
     *
     * @method closeDropdown
     * @public
     */
    closeDropdown() {
      this.isExpanded = false;

      document.body.removeEventListener(Events.CLICK, e => this.handleOffClick(e));
      document.body.removeEventListener(Events.KEYDOWN, e => this.handleBodyKeydown(e));
    },

    /**
     * Handles selection of an item from a click or keypress.
     *
     * @method selectItem
     * @param {Number} key - The key of the item to select
     * @public
     */
    selectItem(key) {
      const item = this.$refs[key];
      const link = item.querySelector('.dropdown-list-link');

      if (link) {
        link.click();
      }

      this.closeDropdown();
      this.selected = item.children[0].textContent;
      this.selectedIndex = key;
    },

    /**
     * Resets the element to the default value.
     *
     * Exists to be used by parent elements, such as filters.
     */
    reset() {
      this.selectItem(0);
    },

    /**
     * Set the element to a specific value.
     *
     * Exists to be used by parent elements, such as filters.
     *
     * @param {String} value
     *   The human readable value to set the element to.
     */
    set(value) {
      const selectedKey = $(this.$refs.list).find(`[data-value="${value}"]`).parent().index();
      if (selectedKey >= 0) {
        this.selectItem(selectedKey);
      }
    },

    /**
     * Closes an open dropdown when a click falls outside of it.
     *
     * @method handleOffClick
     * @param {Object} event - Javascript event object
     * @public
     */
    handleOffClick(e) {
      const target = e.target;
      if (this.$el !== target && !this.$el.contains(target)) {
        this.closeDropdown();
      }
    },

    /**
     * Closes an open dropdown if the escape key is used.
     *
     * @method handleBodyKeydown
     * @param {Object} event - Javascript event object
     * @public
     */
    handleBodyKeydown(e) {
      if (e.keyCode === KeyCodes.ESCAPE) {
        this.closeDropdown();
      }
    },

    /**
     * Handles keypresses on the dropdown toggle.
     *
     * @method handleToggleKeydown
     * @param {Object} event - Javascript event object
     * @public
     */
    handleToggleKeydown(e) {
      this.$refs[0].focus();
    },

    /**
     * Given a keycode, focuses the next relevant item.
     *
     * @method focusItem
     * @param {Number} currentIndex - The index of the currently selected item.
     * @public
     */
    focusItem(index) {
      const item = this.$refs[index];
      if (item) {
        item.focus();
      }
    },

    /**
     * Determines if the list height would extend beyond the visible viewport.
     *
     * @return {Boolean} - If the list will/won't extend beyond the viewport.
     */
    willExtendBeyondViewport() {
      // Avoids opening upwards in situations where an upward list would get cut
      // off by the container's overflow rules. This is only an acceptable
      // solution when there's significant content below the dropdown.
      if (this.$el.classList.contains(CLASSES.IGNORE_VIEWPORT)) {
        return false;
      }

      const $trigger = $(this.$refs.trigger);
      const triggerBottom = $trigger.offset().top + $trigger.outerHeight();
      const listHeight = parseInt($(this.$refs.list).css('max-height'), 10);
      const viewportBottom = $(window).scrollTop() + $(window).height();

      return triggerBottom + listHeight > viewportBottom && listHeight < $(window).height();
    }
  }
})
